// src/encounter/services/use-my-speciality-ids.ts
// [PROD-3336] The signed-in practitioner's own specialities, for the Referral card: it reads the referrals sent to these
// speciality ids, and the PROD-1395 `performerRole=dietitian` rows only for a clinician of the dietetics discipline.
//
// Contract (PROD-3336, Practice.Aggregator, org and user from the token): GET /api/v1/Practice/Specialities/Mine →
// [{ specialityId: guid, name }], allied (staff) specialities included. A failed read is reported as `failed`: the card then
// keeps the dietitian rows (as before PROD-3336), so a dietitian never loses the card to a failed read.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import { alliedDisciplineOf } from '../lib/allied-discipline';

export const MY_SPECIALITIES_PATH = '/api/v1/Practice/Specialities/Mine';

interface MySpecialityRow {
  specialityId?: string | null;
  name?: string | null;
}

export interface MySpecialities {
  ids: string[];
  /** One of the clinician's own specialities is of the dietetics discipline. */
  dietetics: boolean;
  /** False while the read is in flight; true once it answered or failed (or is not asked). */
  settled: boolean;
  /** The read failed: nothing is known about the clinician's specialities. */
  failed: boolean;
}

export function useMySpecialityIds(enabled: boolean): MySpecialities {
  const userId = useAuthStore((s) => s.user?.userId ?? null);
  const organizationId = useAuthStore((s) => s.user?.organizationId ?? null);
  const on = enabled && !!userId;
  const { data, isPending, isError } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['my-specialities', organizationId, userId],
    enabled: on,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }) => {
      const rows = await get<MySpecialityRow[]>(MY_SPECIALITIES_PATH, { signal });
      return (Array.isArray(rows) ? rows : [])
        .map((r) => ({ id: r.specialityId?.trim() ?? '', name: r.name?.trim() ?? '' }))
        .filter((r) => r.id.length > 0);
    },
  });
  if (!on) return { ids: [], dietetics: false, settled: true, failed: true };
  const rows = data ?? [];
  return {
    ids: rows.map((r) => r.id),
    dietetics: rows.some((r) => alliedDisciplineOf(r.name) === 'dietetics'),
    settled: !isPending || isError,
    failed: isError,
  };
}
